import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Customer } from '@nucleus-commerce/api-client/admin';
import { ApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';

import { useDeleteCustomer } from '../hooks/use-customer-mutations';
import { DELETE_CONFIRMATION } from '../schemas/customer-rules';

export interface DeleteCustomerDialogProps {
  storeId: string;
  customer: Customer;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
}

function refusal(error: unknown): { openOrders: number | null } | null {
  if (!(error instanceof ApiError) || error.code !== 'RESOURCE_IN_USE') return null;
  const count = error.meta.openOrders;
  return { openOrders: typeof count === 'number' ? count : null };
}

/**
 * The anonymising deletion: personal data is erased, orders stay with placeholders and the email
 * is freed. It cannot be undone, so staff type a word to confirm.
 */
export function DeleteCustomerDialog({
  storeId,
  customer,
  open,
  onOpenChange,
  onDeleted,
}: DeleteCustomerDialogProps) {
  const { t } = useTranslation(['customers', 'common']);
  const message = useErrorMessage();
  const remove = useDeleteCustomer(storeId);
  const [typed, setTyped] = useState('');
  const blocked = refusal(remove.error);

  const close = () => {
    onOpenChange(false);
    remove.reset();
    setTyped('');
  };

  const submit = () =>
    remove.mutate(customer.id, {
      onSuccess: () => {
        close();
        onDeleted();
      },
    });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('customers:delete.title')}</DialogTitle>
          <DialogDescription>
            {t('customers:delete.description', { email: customer.email })}
          </DialogDescription>
        </DialogHeader>
        {remove.error ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {blocked
                ? blocked.openOrders === null
                  ? t('customers:delete.openOrdersUnknown')
                  : t('customers:delete.openOrders', { count: blocked.openOrders })
                : message(remove.error)}
            </AlertDescription>
          </Alert>
        ) : null}
        <div className="flex flex-col gap-2">
          <Label htmlFor="delete-customer-confirm">
            {t('customers:delete.confirmLabel', { word: DELETE_CONFIRMATION })}
          </Label>
          <Input
            id="delete-customer-confirm"
            autoComplete="off"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={close}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={typed.trim() !== DELETE_CONFIRMATION || remove.isPending}
            onClick={submit}
          >
            {t('customers:delete.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
